import React from 'react';
import { Dialog, Box, Typography, IconButton } from '@mui/material';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CloseIcon from '@material-ui/icons/Close';

/**
 * ErrorDialog
 *
 * Ported from my-dq-web (CourseTime) and adapted to the dq-seal-client MUI stack.
 * A simple, red-themed error popup used to surface "NOK" API errors as a dialog
 * instead of a toast.
 *
 * Props:
 *   isOpen        {boolean}   — controls visibility
 *   onClose       {function}  — called when the user dismisses the dialog
 *   errorMessage  {string}    — message body to display
 *   title         {string}    — header title (default "Error")
 */
const ErrorDialog = ({ isOpen, onClose, errorMessage, title = 'Error' }) => {
    return (
        <Dialog
            open={!!isOpen}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden', m: { xs: 2, sm: 4 } } }}
        >
            {/* ── Header ──────────────────────────────────────────────────── */}
            <Box
                sx={{
                    background: '#fed7d7', borderBottom: '1px solid #feb2b2',
                    px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorOutlineIcon sx={{ color: '#c53030', fontSize: 24 }} />
                    <Typography sx={{ fontFamily: 'Surt, sans-serif', fontWeight: 600, fontSize: '20px', color: '#c53030' }}>
                        {title}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    size="small"
                    sx={{ color: '#c53030', '&:hover': { backgroundColor: '#feb2b2' } }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* ── Content ─────────────────────────────────────────────────── */}
            <Box sx={{ p: 3 }}>
                <Typography
                    sx={{ fontFamily: 'Surt, sans-serif', fontSize: '16px', lineHeight: 1.5, color: '#2d3748', mb: 3 }}
                >
                    {errorMessage}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Box
                        component="button"
                        onClick={onClose}
                        sx={{
                            background: '#c53030', color: '#fff', border: 'none', borderRadius: '6px',
                            px: '20px', py: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                            fontFamily: 'Surt, sans-serif', transition: 'all 0.2s ease',
                            '&:hover': { background: '#9b2c2c' },
                            '&:active': { transform: 'scale(0.98)' },
                        }}
                    >
                        Close
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default ErrorDialog;
